// Thư mục: src/stores/driverDashboardStore.js
import { defineStore } from "pinia";
import { XePhanCongApi } from "@/services/xePhanCongService"; // Dùng service trực tiếp
import { HoSoXeApi } from "@/services/hoSoXeService";
import { XeApi } from "@/services/xeService"; // Cần để lấy ODO
import driverJobService from "@/services/driverJobService";
// Định nghĩa các bước của luồng
const STEPS = {
  LOADING: 0,
  NO_ASSIGNMENT: 1, // Không có xe
  ASSIGNED: 2, // Có xe, chờ nhận
  CHECKLIST: 3, // Đã nhận, chờ checklist
  ON_DUTY: 4, // Đã checklist, đang làm việc
  CHECK_OUT: 5, // Bấm trả xe, chờ nhập ODO
  ERROR: 99,
};

export const useDriverDashboardStore = defineStore("driverDashboard", {
  state: () => ({
    historyList: [], // <--- Thêm dòng này để chứa danh sách lịch sử
    isHistoryLoading: false,

    step: STEPS.LOADING, // Trạng thái hiện tại của tài xế
    error: null,
    isLoading: false, // 👈 THÊM DÒNG NÀY

    // Dữ liệu tổng hợp
    currentAssignment: null, // XePhanCongDto
    hoSo: null, // HoSoXeResponse (Tài xế + Giấy tờ)
    currentOdo: null, // Số ODO mới nhất
  }),
  getters: {
    STEPS: () => STEPS, // Trả về hằng số STEPS để component dùng

    // Getter tiện ích
    xeHienTai(state) {
      return state.hoSo?.xeHienTai || null;
    },
    taiXeHienTai(state) {
      return state.hoSo?.taiXe || null;
    },
    giayToXe(state) {
      return state.hoSo?.danhSachGiayTo?.filter((gt) => gt.xeId) || [];
    },
    giayToTaiXe(state) {
      return state.hoSo?.danhSachGiayTo?.filter((gt) => gt.nguoiDungId) || [];
    },
  },
  actions: {
    /**
     * Action chính: Tải tất cả thông tin khi vào trang
     */
    async initializeDashboard() {
      this.step = STEPS.LOADING;
      this.error = null;

      try {
        // 1. Lấy phân công hiện tại
        let assignment = null;
        try {
          assignment = await XePhanCongApi.myCurrent(); // backend đã trả checklistDoneToday
          this.currentAssignment = assignment;
        } catch (e) {
          if (e?.response?.status === 404) {
            this.step = STEPS.NO_ASSIGNMENT;
            this.currentAssignment = null;
          } else {
            throw e;
          }
        }

        // 2. Lấy hồ sơ tài xế + xe
        this.hoSo = await HoSoXeApi.getHoSoCuaToi();

        // 3. Quyết định step
        if (this.currentAssignment) {
          const trangThai = this.currentAssignment.trangThai;
          const checklistDone = !!this.currentAssignment.checklistDoneToday;

          if (trangThai === "ASSIGNED") {
            this.step = STEPS.ASSIGNED;
          } else if (trangThai === "IN_USE") {
            if (!checklistDone) {
              this.step = STEPS.CHECKLIST; // 🔒 chưa checklist trong ca này → bắt vào checklist
            } else {
              this.step = STEPS.ON_DUTY;
            }
          } else {
            this.step = STEPS.NO_ASSIGNMENT;
          }
        } else {
          this.step = STEPS.NO_ASSIGNMENT;
        }
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          e?.message ||
          "Lỗi tải dữ liệu dashboard";
        this.step = STEPS.ERROR;
        console.error("initializeDashboard error:", e);
      }
    },

    /**
     * Action: Tài xế bấm "Nhận Xe" (Step 1 -> 2)
     */
    async nhanXe() {
      if (this.step !== STEPS.ASSIGNED) return;
      this.isLoading = true;
      this.error = null;
      try {
        const payload = {
          phanCongId: this.currentAssignment.id,
          eventType: "RECEIVE",
          // Không cần ODO khi nhận
        };
        await XePhanCongApi.postEvent(payload);

        // Cập nhật state
        this.currentAssignment.trangThai = "IN_USE"; // Cập nhật cục bộ
        this.step = STEPS.CHECKLIST; // *** Chuyển sang bước Checklist ***
      } catch (e) {
        this.error =
          e?.response?.data?.message || e?.message || "Lỗi khi nhận xe";
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Action: Tài xế hoàn tất Checklist (Step 2 -> 3)
     * (Component Checklist sẽ gọi hàm này)
     */
    handleChecklistSuccess() {
      if (this.step !== STEPS.CHECKLIST) return;
      if (this.currentAssignment) {
        this.currentAssignment.checklistDoneToday = true;
      }
      this.step = STEPS.ON_DUTY;
    },

    /**
     * Action: Tài xế bấm "Trả Xe" (Step 3 -> 4)
     */
    openCheckoutModal() {
      if (this.step !== STEPS.ON_DUTY) return;
      this.step = STEPS.CHECK_OUT; // Chuyển sang step chờ nhập ODO
      this.error = null; // Xóa lỗi cũ
    },
    cancelCheckout() {
      if (this.step !== STEPS.CHECK_OUT) return;
      this.step = STEPS.ON_DUTY; // Quay lại
    },

    /**
     * Action: Tài xế Xác nhận Trả Xe (Step 4 -> 1)
     */
    async traXe(payload) {
      // payload = { odo, mucNhienLieu, ghiChu }
      if (this.step !== STEPS.CHECK_OUT) return;
      this.isLoading = true;
      this.error = null;

      try {
        const odoInput = payload.odo; // có thể null / undefined

        // ODO KHÔNG BẮT BUỘC:
        // chỉ kiểm tra nếu tài xế CÓ nhập số
        const odoGanNhat = this.hoSo?.xeHienTai?.odoGanNhat;
        if (
          odoInput !== null &&
          odoInput !== undefined &&
          odoGanNhat &&
          odoInput < odoGanNhat
        ) {
          throw new Error(
            `Số ODO trả xe (${odoInput}) không thể nhỏ hơn ODO đã ghi nhận (${odoGanNhat}).`
          );
        }

        const apiPayload = {
          phanCongId: this.currentAssignment.id,
          eventType: "RETURN",
          // chỉ gửi odo nếu có nhập
          ...(odoInput !== null && odoInput !== undefined
            ? { odo: odoInput }
            : {}),
          mucNhienLieu: payload.mucNhienLieu,
          ghiChu: payload.ghiChu,
        };

        await XePhanCongApi.postEvent(apiPayload);

        // Trả xe thành công, reset toàn bộ dashboard
        await this.initializeDashboard();
      } catch (e) {
        this.error =
          e?.response?.data?.message || e?.message || "Lỗi khi trả xe";
        throw e;
      } finally {
        this.isLoading = false;
      }
    },

    async submitDriverJob(payload) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await driverJobService.submitJob(payload);
        return response.data; // Trả về data từ BE
      } catch (err) {
        this.error = err.response?.data?.message || "Lỗi khi gửi báo cáo";
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchHistory(taiXeId) {
      if (!taiXeId) return;
      this.isHistoryLoading = true;
      try {
        const res = await driverJobService.getHistory({ taiXeId });
        // Xử lý dữ liệu trả về từ API
        if (res.data && res.data.data) {
          this.historyList = res.data.data;
        } else if (Array.isArray(res.data)) {
          this.historyList = res.data;
        } else {
          this.historyList = [];
        }
      } catch (err) {
        console.error("Lỗi tải lịch sử:", err);
      } finally {
        this.isHistoryLoading = false;
      }
    },
  },
});
